import { ChangeEvent, RefObject, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  areExpirationMonthPartsBlank,
  expirationMonthPartsToValue,
  expirationMonthValidationMessage,
  parseExpirationMonthParts,
  type ExpirationMonthParts
} from "../lib/expiration";
import { sanitizeDateInputPart } from "./DateInput";

export function ExpirationMonthInput({
  label,
  value,
  onChange,
  required,
  disabled,
  allowEmpty,
  hideLabel
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  allowEmpty?: boolean;
  hideLabel?: boolean;
}) {
  const id = useId();
  const blankAllowed = allowEmpty ?? !required;
  const [parts, setParts] = useState<ExpirationMonthParts>(() => parseExpirationMonthParts(value));
  const monthRef = useRef<HTMLInputElement | null>(null);
  const yearRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setParts(parseExpirationMonthParts(value));
  }, [value]);

  const validationMessage = useMemo(
    () => expirationMonthValidationMessage(parts, { allowEmpty: blankAllowed, required }),
    [blankAllowed, parts, required]
  );

  useEffect(() => {
    yearRef.current?.setCustomValidity(validationMessage);
  }, [validationMessage]);

  useEffect(() => {
    const nextValue = expirationMonthPartsToValue(parts);
    if (nextValue) {
      if (nextValue !== value) onChange(nextValue);
      return;
    }
    if (blankAllowed && areExpirationMonthPartsBlank(parts) && value) onChange("");
  }, [blankAllowed, onChange, parts, value]);

  const updatePart = (
    part: keyof ExpirationMonthParts,
    maxLength: number,
    raw: string,
    nextRef?: RefObject<HTMLInputElement>
  ) => {
    const nextPart = sanitizeDateInputPart(raw, maxLength);
    setParts((previous) => ({ ...previous, [part]: nextPart }));
    if (nextPart.length >= maxLength) {
      window.setTimeout(() => nextRef?.current?.focus(), 0);
    }
  };

  return (
    <fieldset className="date-input-field stack-xs" disabled={disabled}>
      <legend className={`label${hideLabel ? " sr-only" : ""}`}>{label}</legend>
      <div className="segmented-date-input" role="group" aria-label={label}>
        <input
          ref={monthRef}
          id={`${id}-month`}
          className="input date-part-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="MM"
          value={parts.month}
          maxLength={2}
          required={required || !blankAllowed}
          aria-label={`${label} month`}
          aria-invalid={Boolean(validationMessage)}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event: ChangeEvent<HTMLInputElement>) => updatePart("month", 2, event.target.value, yearRef)}
        />
        <span className="date-separator" aria-hidden>
          /
        </span>
        <input
          ref={yearRef}
          id={`${id}-year`}
          className="input date-part-input date-year-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="YYYY"
          value={parts.year}
          maxLength={4}
          required={required || !blankAllowed}
          aria-label={`${label} year`}
          aria-invalid={Boolean(validationMessage)}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event: ChangeEvent<HTMLInputElement>) => updatePart("year", 4, event.target.value)}
        />
      </div>
    </fieldset>
  );
}
